import React, { useCallback, useEffect, useId, useMemo } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import cn from "classnames";

import Input from "../../../common/input";
import CalendarWithInputMask from "../../../listingDetail/calendarWithInputMask";
import SelectInput from "../../../common/selectInput";
import { getCountryNameFromCode, getStateNameFromCode, renderSelectCountries, renderSelectStates } from "../../../../helpers/select";
import { Modal } from "../../../common/modal";
import { Button } from "../../../common/button";
import { actions as actionProfile } from "../../../../actions/account/profile";
import { actions as mainActions } from "../../../../actions/main";
import { OverlayPreloader } from "../../../common/preloader";
import { formatBirthDate, splitFullName } from "../../../../helpers/questionForm";
import { parseBirthDate } from "../../../../helpers/birthDate";
import styles from "./questionForm.module.css";

const QuestionForm = ({ closeModal, formValues = {} }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const labelIdPrefix = useId();
  const nameLabelId = `${labelIdPrefix}-name-label`;
  const birthDateLabelId = `${labelIdPrefix}-birth-date-label`;
  const zipLabelId = `${labelIdPrefix}-zip-label`;
  const cityLabelId = `${labelIdPrefix}-city-label`;

  const { countries, states } = useSelector(state => state.main);
  const { preloaders } = useSelector(state => state.account);
  const getCountriesAndStates = useCallback(() => dispatch(mainActions.getCountriesAndStates()), [dispatch]);
  const updateContacts = useCallback(data => dispatch(actionProfile.updateContacts(data, true)), [dispatch]);

  const validate = values => {
    const { name } = values;
    const errors = {};

    const requiredText = t("dashboard_common_required");

    if (!name.trim()) {
      errors.name = requiredText;
    }

    return errors;
  };

  const formik = useFormik({
    initialValues: {
      name: formValues.name || "",
      birth_date: formValues.birth_date || "",
      location: formValues.location || "",
      selectedLocation: formValues.selectedLocation,
      state:
        typeof formValues.state === "string"
          ? { value: formValues.state, label: getStateNameFromCode(states, formValues.state) }
          : formValues.state || "",
      country:
        typeof formValues.country === "string"
          ? { value: formValues.country, label: getCountryNameFromCode(countries, formValues.country) }
          : formValues.country || "",
      city: formValues.city || "",
      zip: formValues.zip || ""
    },
    enableReinitialize: true,
    onSubmit: values => {
      const { name, birth_date, state, country, city, zip } = values;

      updateContacts({
        ...splitFullName(name),
        birth_date: formatBirthDate(birth_date),
        country: country?.value,
        city,
        state: state?.value,
        zip
      });
    },
    validate
  });

  const { values, errors, setFieldValue, handleSubmit } = formik;
  const isCanadaOrUS = useMemo(() => {
    const countryValue = values.country?.value;
    const countryLabel = values.country?.label || (countryValue ? getCountryNameFromCode(countries, countryValue) : "");
    return ["United States", "Canada"].includes(countryLabel);
  }, [countries, values.country?.label, values.country?.value]);
  const isCountrySelected = useMemo(() => Boolean(values.country?.value || values.country?.label), [
    values.country?.value,
    values.country?.label
  ]);

  useEffect(() => {
    const hasCountries = countries?.length;
    const hasStates = states && Object.keys(states).length;
    if (!hasCountries || !hasStates) {
      getCountriesAndStates();
    }
  }, [countries?.length, states, getCountriesAndStates]);

  const handleChange = event => {
    const { name, value } = event.target;

    setFieldValue(name, value);
  };

  const handleChangeDate = date => {
    setFieldValue("birth_date", date);
  };

  const handleSelectDate = date => {
    setFieldValue("birth_date", date);
  };

  const handleSelectCountry = newValue => {
    setFieldValue("country", newValue);

    if (!isCanadaOrUS) {
      setFieldValue("state", "");
    }
  };

  const handleSelectState = newValue => {
    setFieldValue("state", newValue);
  };

  const getCountryValue = countryValue => {
    if (typeof countryValue === "string") {
      return [{ value: countryValue, label: getCountryNameFromCode(countries, countryValue) }];
    }

    return [countryValue];
  };

  const getStateValue = stateValue => {
    if (typeof stateValue === "string") {
      return [{ value: stateValue, label: getStateNameFromCode(states, stateValue) }];
    }

    return [stateValue];
  };

  return (
    <Modal
      classNameButton={styles.closeButton}
      classNameMain={styles.modal}
      closeModal={closeModal}
      clearAllModals={closeModal}
      isDisableCloseOutside
    >
      {preloaders.contacts && <OverlayPreloader />}

      <div className={styles.header}>
        <h2 className={styles.title}>Please answer a few questions.</h2>
      </div>

      <div className={styles.formContent}>
        <label id={nameLabelId} className={styles.label}>
          What is your full name? <span aria-hidden="true">*</span>
        </label>
        <Input
          ariaLabelledBy={nameLabelId}
          isInvalid={errors.name}
          error={errors.name}
          name="name"
          className={styles.input}
          placeholder=""
          onChange={handleChange}
          value={values.name}
        />
      </div>
      <div className={styles.formContent}>
        <label id={birthDateLabelId} className={styles.label}>
          What is your birthdate?
        </label>

        <CalendarWithInputMask
          ariaLabelledBy={birthDateLabelId}
          error={errors.birth_date}
          isInvalid={errors.birth_date}
          value={parseBirthDate(values.birth_date)}
          onChange={date => handleSelectDate(date)}
          handleChangeInput={handleChangeDate}
          placeholder="MM/DD/YYYY"
          defaultMaxDate={new Date()}
          inputClassName={styles.calendarInput}
          minDate={new Date("1899")}
        />
      </div>

      <div className={styles.formContent}>
        <label className={styles.label}>Where do you live?</label>

        <SelectInput
          tabSelectsValue={false}
          classNameWrap={styles.select}
          name="country"
          ariaLabel={t("dashboard_common_country")}
          placeholder="Select a country"
          onChange={handleSelectCountry}
          options={renderSelectCountries(countries)}
          value={values.country?.value ? getCountryValue(values.country?.value) : ""}
        />
      </div>

      {isCountrySelected ? (
        isCanadaOrUS ? (
          <div className={styles.formContent}>
            <label id={zipLabelId} className={styles.label}>
              Zip code
            </label>
            <Input
              ariaLabelledBy={zipLabelId}
              isInvalid={errors.zip}
              error={errors.zip}
              name="zip"
              className={styles.input}
              placeholder="Enter zip code"
              onChange={handleChange}
              value={values.zip}
            />
          </div>
        ) : (
          <>
            <div className={styles.formContent}>
              <label className={styles.label}>Select a state</label>

              <SelectInput
                tabSelectsValue={false}
                classNameWrap={styles.select}
                noOptionsMessage={() => t("dashboard_referrals_select_country_first")}
                name="state"
                ariaLabel={t("dashboard_common_state")}
                placeholder="Select a state"
                onChange={handleSelectState}
                options={values.country?.value && states?.length ? renderSelectStates(values.country.value, states) : []}
                value={values.state.value ? getStateValue(values.state) : ""}
              />
            </div>
            <div className={styles.formContent}>
              <label id={cityLabelId} className={styles.label}>
                Enter a City
              </label>

              <Input
                ariaLabelledBy={cityLabelId}
                isInvalid={errors.city}
                error={errors.city}
                name="city"
                className={styles.input}
                placeholder="Type your city"
                onChange={handleChange}
                value={values.city}
              />
            </div>
          </>
        )
      ) : null}

      <Button className={cn(styles.submit, styles.submitPrimary)} onClick={() => handleSubmit()}>
        Next
      </Button>
    </Modal>
  );
};

export default QuestionForm;
